import { useApolloClient } from '@apollo/client';
import { Divider, Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    CreateUserlikeChatbotSettingsDocument,
    type CreateUserlikeChatbotSettingsMutation,
    type CreateUserlikeChatbotSettingsMutationVariables,
} from '../../../../api/mutations/createUserlikeChatbotSettings';
import { type UserlikeChatbotSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type AddUserlikeChatbotSettingsFormProps = {
    moduleId: string;
    onComplete: () => void;
};

type FormValues = UserlikeChatbotSettingsInput;

const initialValues: FormValues = {
    displayName: '',
    script: '',
};

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('script')
);

const AddUserlikeChatbotSettingsForm = ({ moduleId, onComplete }: AddUserlikeChatbotSettingsFormProps) => {
    const { t } = useTranslation(['userlikeChatbotModuleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('userlikeChatbotModuleDetails:settingsDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<CreateUserlikeChatbotSettingsMutation, CreateUserlikeChatbotSettingsMutationVariables>({
                    mutation: CreateUserlikeChatbotSettingsDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('userlikeChatbotModuleDetails:settingsDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, moduleId]
    );

    const validate = useValidator(validator);

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="addUserlikeChatbotSettings"
                    layout="vertical"
                    name="addUserlikeChatbotSettings"
                    onSubmitCapture={handleSubmit}
                >
                    <Divider>{t('userlikeChatbotModuleDetails:dividers.mainDetails')}</Divider>
                    <InputField
                        {...t('userlikeChatbotModuleDetails:settingsDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                    <InputField
                        {...t('userlikeChatbotModuleDetails:settingsDrawer.fields.script', {
                            returnObjects: true,
                        })}
                        name="script"
                    />
                </Form>
            )}
        </Formik>
    );
};

export default AddUserlikeChatbotSettingsForm;
